<template>
  <div class="whatsapp-tab">
    <!-- Non-fatal errors only (a failed connect/request while the tab still has a state to
         show). Requires `status`, because with no status there is no state behind the alert
         and dismissing it would leave an EMPTY pane — that case gets its own branch below. -->
    <b-alert
      :show="!!errorMessage && !!status"
      variant="danger"
      dismissible
      class="wst-alert"
      @dismissed="errorMessage = ''"
    >
      {{ errorMessage }}
    </b-alert>

    <!-- ==================== Loading ==================== -->
    <div v-if="loading" class="wst-loading">
      <b-spinner small class="mr-2" />
      Loading your WhatsApp status&hellip;
    </div>

    <!-- ==================== Could not load status ====================
         Terminal for the tab: with no status every other branch is false, so without this
         the pane renders nothing at all. Carries its own copy + retry rather than relying
         on the dismissible alert, which the customer can dismiss into a blank tab. -->
    <b-card v-else-if="!status" class="wst-card">
      <div class="wst-hero">
        <div class="wst-hero__icon wst-hero__icon--error">!</div>
        <div class="wst-hero__body">
          <h4 class="wst-hero__title">We couldn&rsquo;t load your WhatsApp status</h4>
          <p class="wst-live-copy">
            This is usually a temporary connection problem. Try again in a moment &mdash;
            if it keeps happening, contact Teamio.
          </p>
          <b-button variant="primary" class="wst-cta" @click="refreshStatus">
            Try again
          </b-button>
        </div>
      </div>
    </b-card>

    <!-- ==================== Self-serve wizard ====================
         Renders INSTEAD of every card below when the server says this caller may drive it
         (`my-status.wizard.enabled` — super-admins only during the driven phase, decision A3).
         Placed here, above the card chain, so a practice WITHOUT the wizard sees exactly the
         tab it saw yesterday: the four cards below are untouched. -->
    <WhatsAppWizard
      v-else-if="showWizard"
      :status="status"
      :site-id="siteID"
      :site-name="siteName"
      :connecting="connecting"
      :sdk-ready="sdkReady"
      @refresh="refreshStatus"
      @connect="connect"
    />

    <!-- ==================== Pitch (not_eligible + not_started) ====================
         Both states sell the same feature and differ ONLY in the call to action, so the
         benefits live in one block instead of two copies free to drift apart. -->
    <b-card v-else-if="showPitch" class="wst-card">
      <div class="wst-hero">
        <div class="wst-hero__icon">&#x1F4AC;</div>
        <div class="wst-hero__body">
          <h4 class="wst-hero__title">Message patients on WhatsApp</h4>

          <ul class="wst-benefits">
            <li>Reach patients who prefer WhatsApp &mdash; including international and travelling patients.</li>
            <li>Replies land in your existing Conversations inbox, right alongside your text messages.</li>
            <li>Uses the credits you already have &mdash; a flat 1 credit per WhatsApp message.</li>
          </ul>

          <!-- CTA A: ready to self-serve -->
          <template v-if="tabState === 'not_started'">
            <!-- The same number-naming steps as the wizard and the link page (2026-09-11): this button
                 opens the same Facebook sign-in, which asks for a phone number and takes any. -->
            <WhatsAppSignInSteps class="wst-signin" :phone="pitchNumber" />
            <b-button
              variant="success"
              class="wst-cta"
              :disabled="!sdkReady || connecting"
              @click="connect"
            >
              <span v-if="connecting"><b-spinner small class="mr-2" />Working&hellip;</span>
              <span v-else-if="!sdkReady"><b-spinner small class="mr-2" />Loading&hellip;</span>
              <span v-else>Connect WhatsApp</span>
            </b-button>
            <p class="wst-fineprint">
              You&rsquo;ll sign in with Facebook to create your practice&rsquo;s own WhatsApp
              Business account. Takes about a minute &mdash; Teamio handles the rest.
            </p>
          </template>

          <!-- CTA B: texting (the prerequisite) isn't set up yet -->
          <template v-else>
            <p class="wst-prereq">
              WhatsApp runs on your practice&rsquo;s Teamio text number, and this practice
              doesn&rsquo;t have texting set up yet. Our team can get both turned on for you.
            </p>
            <p v-if="requested" class="wst-thanks">{{ thanksLine }}</p>
            <b-button
              v-else
              variant="primary"
              class="wst-cta"
              :disabled="requesting"
              @click="requestWhatsApp"
            >
              <span v-if="requesting"><b-spinner small class="mr-2" />Sending&hellip;</span>
              <span v-else>Get in touch to enable</span>
            </b-button>
          </template>
        </div>
      </div>
    </b-card>

    <!-- ==================== In progress ==================== -->
    <b-card v-else-if="tabState === 'in_progress'" class="wst-card">
      <h4 class="wst-card__title">Setting up WhatsApp for your practice</h4>

      <div class="wst-stepper">
        <div
          v-for="(m, i) in milestones"
          :key="m.key"
          class="wst-step"
          :class="'is-' + m.state"
        >
          <div v-if="i < milestones.length - 1" class="wst-bar" />
          <div class="wst-dot">
            <template v-if="m.state === 'done'">&#x2713;</template>
            <template v-else>{{ i + 1 }}</template>
          </div>
          <div class="wst-lbl">{{ m.label }}</div>
        </div>
      </div>

      <p v-if="progressLine" class="wst-progress">{{ progressLine }}</p>

      <!-- Setup finishes on Teamio's side, so nothing pushes to this tab. Without a manual
           re-check the only way forward is a full page reload. -->
      <div class="wst-actions">
        <b-button variant="link" size="sm" :disabled="loading" @click="refreshStatus">
          Refresh status
        </b-button>
      </div>
    </b-card>

    <!-- ==================== Live ==================== -->
    <b-card v-else-if="tabState === 'live'" class="wst-card">
      <div class="wst-hero">
        <div class="wst-hero__icon wst-hero__icon--live">&#x2713;</div>
        <div class="wst-hero__body">
          <h4 class="wst-hero__title">WhatsApp is active for your practice</h4>
          <p class="wst-live-copy">
            Messages your patients send on WhatsApp show up in Conversations alongside your
            text messages. To answer on WhatsApp, pick WhatsApp in the reply options at the
            top of the conversation.
          </p>
          <p class="wst-live-copy">
            Your appointment reminders keep going out exactly as they do today unless you
            ask us to change them.
          </p>
          <p class="wst-live-note">
            Don't see WhatsApp in your reply options? Log out and back in.
          </p>
        </div>
      </div>
    </b-card>

    <!-- ==================== Turned off ====================
         A practice we deliberately switched off is NOT mid-provisioning. Without this branch
         its completed setup (step stays 'live') derived in_progress, so the tab promised a
         setup that had already finished and then been reversed. Neutral on purpose: nothing
         was lost, and turning it back on is a conversation, not a self-serve wizard — so this
         reuses the same interest ping as the pitch card rather than offering Connect. -->
    <b-card v-else-if="tabState === 'disabled'" class="wst-card">
      <div class="wst-hero">
        <div class="wst-hero__icon wst-hero__icon--off">&#x1F4AC;</div>
        <div class="wst-hero__body">
          <h4 class="wst-hero__title">WhatsApp is turned off for your practice</h4>
          <p class="wst-live-copy">
            Your WhatsApp setup is saved &mdash; nothing was lost. Want it back on? Get in
            touch and we&rsquo;ll re-enable it.
          </p>
          <p v-if="requested" class="wst-thanks">{{ thanksLine }}</p>
          <b-button
            v-else
            variant="primary"
            class="wst-cta"
            :disabled="requesting"
            @click="requestWhatsApp"
          >
            <span v-if="requesting"><b-spinner small class="mr-2" />Sending&hellip;</span>
            <span v-else>Get in touch</span>
          </b-button>
        </div>
      </div>
    </b-card>

    <!-- ==================== WhatsApp messages (live only) ====================
         Sits AFTER the v-if chain, not inside the live card's branch. Since the wizard went GA
         (2026-09-03) every admitted caller gets <WhatsAppWizard> — whose live screen is
         WizardLiveStep — INSTEAD of the live b-card above, so a card nested in that branch would
         be unreachable. `showMessagesCard` is the one gate: WhatsApp live, status loaded, tab not
         mid-refresh. Whichever live screen rendered, this lands directly beneath it. -->
    <WhatsAppMessagesCard v-if="showMessagesCard" :siteID="siteID" />
  </div>
</template>

<script>
/**
 * Customer-facing WhatsApp self-start tab (My Account → WhatsApp).
 *
 * The customer twin of the operator panel (Gautam/components/WhatsAppOnboarding.vue): same
 * backend, but every operator detail — sender SIDs, raw Meta errors, step names — is
 * deliberately absent. This tab only ever shows the five states below (the fifth, 'disabled',
 * is a site we switched off on purpose — distinct from one still being provisioned).
 *
 * All state derivation is imported from store/whatsappSelfStart.js (pure + mirror-tested);
 * nothing about the step vocabulary is re-typed here, so a backend step change can't leave
 * this tab quietly showing the wrong thing.
 *
 * Flag-gated by VUE_APP_WHATSAPP_SELF_START at the MyAccount wiring, not here.
 *
 * ── THE WIZARD (2026-09-02) ──────────────────────────────────────────────────────────────────────
 * When `my-status` says `wizard.enabled`, this tab renders <WhatsAppWizard> INSTEAD of the cards
 * below — the full self-serve flow, gated server-side to operators during the driven phase
 * (decision A3). Everyone else keeps this file's four cards, unchanged; that is the whole point of
 * putting the switch at the top of the v-if chain rather than reworking the cards.
 *
 * This component stays the owner of the Meta SDK, `FB.login`, the postMessage listener and the
 * signup-callback POST. The wizard's connect screen emits `connect` and this runs the same connect
 * it always has. Duplicating the loader would mean two script tags racing for `window.FB` and two
 * listeners answering one postMessage.
 */
import { mapState, mapGetters } from 'vuex';
import axios from 'axios';
import { deriveTabState, deriveWizardStep, milestoneStates, statusLine } from '@/store/whatsappSelfStart';
import { buildFbLoginConfig, extractSignupResult } from '@/pages/WhatsAppConnect/embeddedSignup';
import { loadMetaSdk } from '@/utils/metaSdk';
import { getIdToken } from './firebaseIdToken';
import WhatsAppWizard from './whatsapp-wizard/WhatsAppWizard.vue';
import WhatsAppMessagesCard from './WhatsAppMessagesCard.vue';
import WhatsAppSignInSteps from '@/pages/WhatsAppConnect/WhatsAppSignInSteps.vue';

axios.defaults.baseURL = process.env.VUE_APP_TEAMIO_API_URL;

export default {
  name: 'WhatsAppTab',

  components: { WhatsAppWizard, WhatsAppSignInSteps, WhatsAppMessagesCard },

  data() {
    return {
      status: null,
      loading: true,
      connecting: false,
      sdkReady: false,
      sdkLoading: false,
      signupToken: '',
      errorMessage: '',
      requested: false,
      requesting: false,
    };
  },

  computed: {
    // siteID is the ONLY store value this tab needs, so it maps that one directly rather
    // than pulling in the global mixin — whose created() hook would re-run the settings +
    // employees fetches on every mount of this tab. Same call the sibling SmsCreditsTab
    // documents in this folder.
    ...mapState('customer', ['siteID']),
    // Same reasoning as above: ONE getter, not the global mixin. Used only to prefill the wizard's
    // display-name field with the practice's own name.
    ...mapGetters('customer', ['getSite']),

    siteName() {
      const site = this.siteID ? this.getSite(this.siteID) : null;
      return (site && site.Name) || '';
    },

    /**
     * Render the wizard instead of the cards.
     *
     * Server-enforced (A3): `wizard.enabled` is true only for callers the backend will actually let
     * drive the wizard endpoints, so this can never show a practice a flow whose every POST 403s.
     *
     * `deriveWizardStep` returning null is the second half of the condition, and it is what keeps
     * a deliberately switched-off site on this file's neutral "turned off" card. Re-enabling is a
     * conversation, not a wizard — the practice's WhatsApp account, sender and approved template
     * all still exist — so the wizard has nothing to drive there and says so by returning null,
     * rather than growing a second copy of that card's copy.
     */
    showWizard() {
      return !!(this.status && this.status.wizard && this.status.wizard.enabled)
        && deriveWizardStep(this.status) !== null;
    },

    /** The number the pitch card's sign-in steps name — my-status `whatsappNumber` (GENERAL_PHONE). */
    pitchNumber() {
      return (this.status && this.status.whatsappNumber) || '';
    },

    /** Which wizard screen is showing, or null when the wizard is not rendering at all. */
    wizardStepKey() {
      return this.showWizard ? deriveWizardStep(this.status) : null;
    },

    /**
     * Meta's SDK is NOT loaded speculatively — only the states with a Connect button pay for a
     * third-party script. That is the pre-existing rule; the wizard just adds a second state that
     * needs it (its connect screen), so the condition became a computed instead of a raw
     * `tabState === 'not_started'` check. Non-wizard behaviour is byte-for-byte what it was.
     */
    needsMetaSdk() {
      if (this.showWizard) return this.wizardStepKey === 'connect';
      return this.tabState === 'not_started';
    },

    tabState() {
      if (!this.status) return null;
      return deriveTabState({
        eligible: this.status.eligible,
        enabled: this.status.enabled,
        step: this.status.step,
        disabled: this.status.disabled,
      });
    },

    /**
     * Show the "WhatsApp messages" card: only while WhatsApp is live (`tabState === 'live'` — true
     * for the wizard's live screen AND the legacy live card, since deriveWizardStep and
     * deriveTabState agree on live), once the status has loaded, and not during a refresh (the
     * chain above hides every card then, and this one follows it). A site that is not live has no
     * approved messages to show.
     */
    showMessagesCard() {
      return !!this.status && !this.loading && !!this.siteID && this.tabState === 'live';
    },

    // not_eligible and not_started share the pitch; only the CTA differs.
    showPitch() {
      return this.tabState === 'not_started' || this.tabState === 'not_eligible';
    },

    // ONE owner for the post-request confirmation. It appears in both the not_eligible pitch
    // and the disabled card, which are separate cards in the v-if chain — the sentence itself
    // is the part that would silently drift into two slightly different wordings.
    thanksLine() {
      return 'Thanks — our team will reach out shortly.';
    },

    milestones() {
      return this.status ? milestoneStates(this.status.step, this.status.enabled) : [];
    },

    progressLine() {
      return this.status ? statusLine(this.status.step, this.status.hasIssue) : '';
    },
  },

  watch: {
    // Deliberate trade: Meta's SDK is NOT loaded speculatively. Only the states with a
    // Connect button pay for a third-party script, so sites that will never show Connect
    // never fetch it. A watcher rather than a one-shot mounted() check because the state
    // can arrive late (first load) or change afterwards — e.g. a site regressing to
    // not_started must re-arm the Connect button, which a mounted()-only check would miss.
    // Watches the computed (see needsMetaSdk) so the wizard's connect screen arms it too.
    needsMetaSdk: {
      immediate: true,
      handler(needed) {
        if (needed) this.ensureSdkLoaded();
      },
    },
  },

  mounted() {
    window.addEventListener('message', this.handleMetaMessage);
    this.refreshStatus();
  },

  beforeDestroy() {
    window.removeEventListener('message', this.handleMetaMessage);
  },

  methods: {
    /**
     * The caller's Firebase ID token. The /wa-onboarding/my-* endpoints verify it server-side, so
     * without it they return 401 and nothing happens.
     *
     * The implementation (including the auth-init race fix — see firebaseIdToken.js) moved out of
     * this file on 2026-09-02, when the wizard became a second component calling the same
     * endpoints. Same behaviour, one copy.
     */
    async authToken() {
      return getIdToken(this.$store.state.getAuth());
    },

    /**
     * Load Meta's JS SDK once. Idempotent: concurrent/repeat calls while a load is in
     * flight are no-ops, and a success is permanent. A FAILURE deliberately clears the
     * in-flight flag so a later call can retry (loadMetaSdk is itself retry-safe).
     */
    ensureSdkLoaded() {
      if (this.sdkReady || this.sdkLoading) return;
      this.sdkLoading = true;
      loadMetaSdk()
        .then(() => { this.sdkReady = true; })
        .catch(() => {
          this.errorMessage = 'We could not load WhatsApp connect. Please refresh and try again.';
        })
        .finally(() => { this.sdkLoading = false; });
    },

    async refreshStatus() {
      // No site in the store yet — there is nothing to ask about. Clear the spinner on the
      // way out: this component starts in loading:true, so a bare early return (as in the
      // operator panel, which starts false) would hang the tab on a permanent loader.
      // Falling through to !status renders the error branch, which offers Try again.
      if (!this.siteID) {
        this.loading = false;
        return;
      }

      this.loading = true;
      // Clear any stale error (matches connect/requestWhatsApp) — without this, a retry
      // that SUCCEEDS would float the previous failure's banner over the loaded state.
      this.errorMessage = '';
      try {
        const idToken = await this.authToken();
        // GET has no body — the token must ride in the header.
        const res = await axios.get('/wa-onboarding/my-status', {
          params: { siteID: this.siteID },
          headers: { Authorization: `Bearer ${idToken}` },
        });
        this.status = res.data;
      } catch (e) {
        console.error('WA self-start: refreshStatus failed:', e);
        this.errorMessage = 'Could not load WhatsApp status. Please refresh.';
      } finally {
        this.loading = false;
      }
    },

    async connect() {
      if (!this.sdkReady || this.connecting || !window.FB) return;
      this.connecting = true;
      this.errorMessage = '';
      try {
        const idToken = await this.authToken();
        // Fresh token on EVERY click — an abandoned popup can never strand a stale one.
        const res = await axios.post('/wa-onboarding/my-connect-init', {
          siteID: this.siteID,
          idToken,
        });
        this.signupToken = res.data.token;

        // VERIFY post-Tech-Provider-approval: the FB.login Embedded Signup contract is
        // assumed from Meta's docs — see buildFbLoginConfig + utils/metaSdk for the one
        // authoritative copy of that caveat. The real ids arrive via postMessage below,
        // not this callback.
        window.FB.login((response) => {
          // Popup closed without finishing — re-enable the button.
          if (!response || response.status !== 'connected') this.connecting = false;
        }, buildFbLoginConfig({
          configId: process.env.VUE_APP_META_CONFIG_ID,
          solutionId: process.env.VUE_APP_TWILIO_SOLUTION_ID,
        }));
      } catch (e) {
        console.error('WA self-start: connect failed:', e);
        this.connecting = false;
        this.errorMessage = e.response && e.response.status === 409
          ? 'WhatsApp signup is already in progress for this practice.'
          : 'Could not start WhatsApp signup. Please try again.';
      }
    },

    handleMetaMessage(event) {
      try {
        let data = event && event.data;
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch (_) {
            return; // not our JSON message
          }
        }
        // extractSignupResult is defensive by contract and never throws.
        const result = extractSignupResult(data);
        if (!result || !this.signupToken) return;
        this.completeSignup(result);
      } catch (err) {
        console.error('WA self-start: error handling Meta signup message:', err);
        this.connecting = false;
        this.errorMessage = 'Something went wrong finishing the connection. Please try again.';
      }
    },

    async completeSignup({ wabaId, phoneNumberId }) {
      try {
        // Reuses the operator flow's public callback — the signed token IS the credential,
        // so this endpoint needs no auth header.
        await axios.post('/wa-onboarding/signup-callback', {
          token: this.signupToken,
          wabaId,
          phoneNumberId,
        });
        this.connecting = false;
        await this.refreshStatus();
      } catch (e) {
        console.error('WA self-start: signup-callback failed:', e);
        this.connecting = false;
        this.errorMessage = 'We connected your account but could not save it. Please try again or contact Teamio.';
      }
    },

    async requestWhatsApp() {
      // Busy-guarded like connect(): each click pings Slack, so a double-click must not
      // send our team two requests for the same practice.
      if (this.requesting) return;
      this.requesting = true;
      this.errorMessage = '';
      try {
        const idToken = await this.authToken();
        await axios.post('/wa-onboarding/request', { siteID: this.siteID, idToken });
        this.requested = true;
      } catch (e) {
        console.error('WA self-start: request failed:', e);
        this.errorMessage = 'Could not send your request. Please try again.';
      } finally {
        this.requesting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* Skinned from the SHARED My Account tab tokens, same as SmsCreditsTab and Patient
   Payments, so every tab reads as one design (2026-08-18 re-skin — see _tokens.scss).
   The ONE colour deliberately left hardcoded is #25d366: that is WhatsApp's own brand
   green and it identifies the channel, so it is not ours to retone. */
@import './PatientPayments/tokens';

.whatsapp-tab {
  max-width: 960px;
}

.wst-alert {
  font-size: 0.88rem;
}

.wst-loading {
  display: flex;
  align-items: center;
  color: $pp-text-muted;
  font-size: 0.9rem;
  padding: 1.5rem 0;
}

.wst-card {
  background: $pp-surface;
  border: 1px solid $pp-border;
  border-radius: $pp-radius;
  box-shadow: $pp-shadow;
}

.wst-card__title {
  font-size: 1.05rem;
  font-weight: 700;
  color: $pp-text-strong;
  margin: 0 0 1.25rem;
}

/* ---- Hero (pitch + live confirmation) ---- */
.wst-hero {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.wst-hero__icon {
  flex: 0 0 auto;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: $pp-radius-sm;
  background: $pp-brand-subtle;
  font-size: 1.5rem;
  line-height: 1;
}

/* WhatsApp's own brand green — it identifies the channel, so it stays as-is. */
.wst-hero__icon--live {
  background: #25d366;
  color: #fff;
  font-weight: 700;
}

.wst-hero__icon--error {
  background: $pp-red-subtle;
  color: $pp-red-text;
  font-weight: 700;
}

/* Deliberately NEUTRAL, not a warning colour: being switched off is a state the practice can
   ask us to reverse, not an error it has to fix. */
.wst-hero__icon--off {
  background: $pp-surface-sunken;
  filter: grayscale(1);
}

.wst-hero__body {
  flex: 1 1 auto;
  min-width: 0;
}

.wst-hero__title {
  font-size: 1.15rem;
  font-weight: 700;
  color: $pp-text-strong;
  margin: 0 0 0.6rem;
}

.wst-benefits {
  margin: 0 0 1.1rem;
  padding-left: 1.1rem;
  color: $pp-text-muted;
  font-size: 0.88rem;
  line-height: 1.6;
  max-width: 640px;

  li + li {
    margin-top: 0.3rem;
  }
}

/* The CTA renders as b-button variant="primary"/"success" — same action either way, so
   both get the one solid brand button of the shared design language. The class sits on
   the b-button's ROOT element, which carries this component's scope attribute, so no
   ::v-deep is needed and this cannot reach another page. */
.wst-cta {
  font-weight: 700;
  border-radius: $pp-radius-sm;

  &.btn-primary,
  &.btn-success {
    background-color: $pp-brand;
    border-color: $pp-brand;
    color: #fff;

    &:hover:not(:disabled),
    &:focus:not(:disabled),
    &:not(:disabled):not(.disabled):active {
      background-color: $pp-brand-deep;
      border-color: $pp-brand-deep;
      color: #fff;
    }
  }
}

/* On the shared steps component's ROOT, which carries this component's scope attribute too. */
.wst-signin {
  margin: 0.9rem 0 1rem;
}

.wst-fineprint {
  margin: 0.75rem 0 0;
  color: $pp-text-subtle;
  font-size: 0.8rem;
  line-height: 1.5;
  max-width: 560px;
}

.wst-prereq {
  margin: 0 0 0.9rem;
  color: $pp-text-muted;
  font-size: 0.88rem;
  line-height: 1.6;
  max-width: 620px;
}

.wst-thanks {
  margin: 0;
  color: $pp-brand-deep;
  font-weight: 600;
  font-size: 0.9rem;
}

/* ---- Live ---- */
.wst-live-copy {
  margin: 0 0 0.6rem;
  color: $pp-text-muted;
  font-size: 0.88rem;
  line-height: 1.6;
  max-width: 640px;
}

.wst-live-note {
  margin: 0.9rem 0 0;
  padding: 0.6rem 0.8rem;
  border-radius: $pp-radius-sm;
  background: $pp-surface-sunken;
  color: $pp-text-muted;
  font-size: 0.83rem;
}

/* ---- Stepper (in progress) ---- */
.wst-stepper {
  display: flex;
  align-items: flex-start;
  margin: 0 2px 1.25rem;
}

.wst-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;
  font-size: 0.75rem;
  color: $pp-text-subtle;
  text-align: center;
}

.wst-dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.78rem;
  font-weight: 700;
  background: $pp-surface;
  border: 2px solid $pp-border;
  color: $pp-text-subtle;
  z-index: 1;
}

/* Completed steps stay on WhatsApp's own green — it reads as "this channel is set up". */
.wst-step.is-done .wst-dot {
  background: #25d366;
  border-color: #25d366;
  color: #fff;
}

.wst-step.is-current .wst-dot {
  background: $pp-brand;
  border-color: $pp-brand;
  color: #fff;
  box-shadow: 0 0 0 4px rgba(15, 114, 89, 0.12);
}

.wst-step.is-done .wst-lbl,
.wst-step.is-current .wst-lbl {
  color: $pp-text-strong;
  font-weight: 600;
}

.wst-lbl {
  margin-top: 0.5rem;
  max-width: 110px;
  line-height: 1.4;
}

.wst-bar {
  position: absolute;
  top: 14px;
  left: 50%;
  width: 100%;
  height: 2px;
  background: $pp-border;
  z-index: 0;
}

.wst-step.is-done .wst-bar {
  background: #25d366;
}

.wst-progress {
  margin: 0;
  color: $pp-text-muted;
  font-size: 0.9rem;
  line-height: 1.6;
}

/* BootstrapVue's .btn-link doesn't carry this component's scope attr, so it's pierced
   with ::v-deep kept UNDER a scoped ancestor — containment holds (repo CSS rule). */
.wst-actions {
  margin-top: 0.75rem;

  ::v-deep .btn-link {
    padding: 0;
    font-size: 0.85rem;
    font-weight: 600;
    color: $pp-brand;

    &:hover { color: $pp-brand-deep; }
  }
}

/* Stack the stepper on narrow screens — four labels never fit side by side on mobile. */
@media (max-width: 575.98px) {
  .wst-stepper {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .wst-step {
    flex-direction: row;
    align-items: center;
    gap: 0.6rem;
    text-align: left;
    width: 100%;
  }

  .wst-lbl {
    margin-top: 0;
    max-width: none;
  }

  .wst-bar {
    display: none;
  }

  .wst-hero {
    flex-direction: column;
  }
}
</style>
